import dayjs, { type Dayjs } from 'dayjs';

import { useLocale } from './useLocale';
import { i18n } from '../plugins';
import { createSingleton } from '../utils';

export const useUnits = createSingleton((): {
    getBandwidth: (bytes?: number, seconds?: number) => string;
    getBytes: (bytes?: number) => string;
    getDuration: (seconds?: number) => string;
    getTime: (seconds?: number, shortened?: boolean) => string;
    getWeekTime: (seconds?: number) => string;
    getGap: (date: Dayjs) => string;
} => {
    const { getLocalizedWeekdays } = useLocale();

    const t: (code: string, params?: Record<string, string | number>) => string = (i18n.value as any).global.t;

    const getBandwidth = (megabytes?: number, seconds?: number): string => {
        if (megabytes == null || isNaN(megabytes) || !isFinite(megabytes) || megabytes <= 0 || seconds == null || isNaN(seconds) || !isFinite(seconds) || seconds <= 0) {
            return `0${t('shared.utils.units.bytes-per-second')}`;
        }
        if (((megabytes / 1000) / seconds) >= 1) {
            return `${+((megabytes / 1000) / seconds).toFixed(2)}${t('shared.utils.units.gigabytes-per-second')}`;
        }
        if ((megabytes / seconds) >= 1) {
            return `${+(megabytes / seconds).toFixed(2)}${t('shared.utils.units.megabytes-per-second')}`;
        }
        if (((megabytes * 1000) / seconds) >= 1) {
            return `${+((megabytes * 1000) / seconds).toFixed(2)}${t('shared.utils.units.kilobytes-per-second')}`;
        }
        if (((megabytes * 1000000) / seconds) >= 1) {
            return `${+((megabytes * 1000000) / seconds).toFixed(2)}${t('shared.utils.units.bytes-per-second')}`;
        }
        return `${+(megabytes / seconds).toFixed(2)}${t('shared.utils.units.megabytes-per-second')}`;
    };

    const getBytes = (megabytes?: number): string => {
        if (megabytes == null || isNaN(megabytes) || !isFinite(megabytes) || megabytes <= 0) {
            return `0${t('shared.utils.units.bytes')}`;
        }
        if ((megabytes / 1000) >= 1) {
            return `${+(megabytes / 1000).toFixed(2)}${t('shared.utils.units.gigabytes')}`;
        }
        if (megabytes >= 1) {
            return `${+megabytes.toFixed(2)}${t('shared.utils.units.megabytes')}`;
        }
        if ((megabytes * 1000) >= 1) {
            return `${+(megabytes * 1000).toFixed(2)}${t('shared.utils.units.kilobytes')}`;
        }
        if ((megabytes * 1000000) >= 1) {
            return `${+(megabytes * 1000000).toFixed(2)}${t('shared.utils.units.bytes')}`;
        }
        return `${+megabytes}${t('shared.utils.units.megabytes')}`;
    };

    const getDuration = (seconds?: number): string => {
        if (seconds == null || isNaN(seconds) || !isFinite(seconds) || seconds <= 0) {
            return `0${t('shared.utils.units.seconds')}`;
        }
        let duration = '';
        if (seconds >= 86400) {
            duration += `${Math.floor(seconds / 86400)}${t('shared.utils.units.days')} `;
            seconds %= 86400;
        }
        if (seconds >= 3600) {
            duration += `${Math.floor(seconds / 3600)}${t('shared.utils.units.hours')} `;
            seconds %= 3600;
        }
        if (seconds >= 60) {
            duration += `${Math.floor((seconds % 3600) / 60)}${t('shared.utils.units.minutes')} `;
            seconds %= 60;
        }
        if (seconds > 0) {
            duration += `${Math.floor(seconds % 60)}${t('shared.utils.units.seconds')} `;
        }
        return duration.trim();
    };

    const getTime = (seconds?: number, shortened = false): string => {
        if (seconds == null || isNaN(seconds) || !isFinite(seconds) || seconds <= 0) {
            return shortened ? '00:00' : '00:00:00';
        }
        if (shortened) {
            return `${Math.floor(seconds / 3600).toString().padStart(2, '0')}:${Math.floor((seconds % 3600) / 60).toString().padStart(2, '0')}`;
        }
        return `${Math.floor(seconds / 3600).toString().padStart(2, '0')}:${Math.floor((seconds % 3600) / 60).toString().padStart(2, '0')}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;
    };

    const getWeekTime = (seconds?: number): string => {
        if (seconds == null || isNaN(seconds) || !isFinite(seconds) || seconds < 0) {
            return '';
        }
        const day = getLocalizedWeekdays()[Math.floor(seconds / 86400)];
        return `${day} ${getTime(seconds % 86400, true)}`;
    };

    const getGap = (date: Dayjs): string => {
        const gap = dayjs.duration(dayjs().diff(date));
        if (gap.asDays() > 30) {
            return t('shared.utils.units.months-ago');
        }
        if (gap.asDays() >= 7) {
            return t('shared.utils.units.weeks-ago', { gap: Math.floor(gap.asWeeks()) });
        }
        if (gap.asDays() >= 1) {
            return t('shared.utils.units.days-ago', { gap: Math.floor(gap.asDays()) });
        }
        if (gap.asHours() >= 1) {
            return t('shared.utils.units.hours-ago', { gap: Math.floor(gap.asHours()) });
        }
        if (gap.asMinutes() >= 1) {
            return t('shared.utils.units.minutes-ago', { gap: Math.floor(gap.asMinutes()) });
        }
        return t('shared.utils.units.seconds-ago', { gap: Math.floor(gap.asSeconds()) });
    };

    return {
        getBandwidth,
        getBytes,
        getDuration,
        getTime,
        getWeekTime,
        getGap
    };
});